import { Observable, Subscription } from 'rxjs'

import ExternalSignerError from '@ambire-common/classes/ExternalSignerError'
import { ExternalSignerController } from '@ambire-common/interfaces/keystore'
import { TypedMessageUserRequest } from '@ambire-common/interfaces/userRequest'
import { normalizeLedgerMessage } from '@ambire-common/libs/ledger/ledger'
import { getHdPathFromTemplate, getHdPathWithoutRoot } from '@ambire-common/utils/hdPath'
import hexStringToUint8Array from '@ambire-common/utils/hexStringToUint8Array'
import { ClearSignContextType, ContextModule, ContextModuleBuilder } from '@ledgerhq/context-module'
import {
  DeviceManagementKitBuilder,
  DeviceModelId as LedgerDeviceModels,
  DiscoveredDevice,
  LEDGER_VENDOR_ID,
  UserInteractionRequired
} from '@ledgerhq/device-management-kit'
import {
  Signature,
  SignerEthBuilder,
  SignTransactionDAStep,
  TypedDataDomain
} from '@ledgerhq/device-signer-kit-ethereum'
import { webHidTransportFactory } from '@ledgerhq/device-transport-kit-web-hid'

import { shouldRetryWithBlindSigning } from './blindSignFallback'
import { isVivaldi } from '@web/constants/browserapi'
import { translation } from '@web/utils/safeTranslation'

export { LedgerDeviceModels }
export type LedgerSignature = Signature

const TIMEOUT_FOR_RETRIEVING_FROM_LEDGER = 5000

class LedgerController implements ExternalSignerController {
  unlockedPath: string = ''

  unlockedPathKeyAddr: string = ''

  isWebHID: boolean

  signerEth: ReturnType<SignerEthBuilder['build']> | null = null

  /**
   * A second signer instance, built with a context module that never returns
   * any clear-signing context. Used as a fallback, see `signTransaction`.
   */
  #blindSignerEth: ReturnType<SignerEthBuilder['build']> | null = null

  #sessionId: string | null = null

  walletSDK: ReturnType<DeviceManagementKitBuilder['build']> | null = null

  type = 'ledger'

  deviceModel: LedgerDeviceModels | 'unknown' = 'unknown'

  deviceId = ''

  static vendorId = LEDGER_VENDOR_ID

  #rejectSigningSubscription: (() => void) | null = null

  constructor() {
    // TODO: Bluetooth support?
    this.isWebHID = true

    // When the `cleanUpListener` method gets passed to the navigator.hid listeners
    // the `this` context gets lost, so we need to bind it here. The `this` context
    // in the `cleanUp` method should be the `LedgerController` instance.
    this.cleanUpListener = this.cleanUpListener.bind(this)
  }

  isUnlocked(path?: string, expectedKeyOnThisPath?: string) {
    // If no path or expected key is provided, just check if there is any
    // unlocked path, that's a valid case when retrieving accounts for import.
    if (!path || !expectedKeyOnThisPath) {
      return !!(this.unlockedPath && this.unlockedPathKeyAddr)
    }

    // Make sure it's unlocked with the right path and with the right key,
    // otherwise - treat as not unlocked.
    return this.unlockedPathKeyAddr === expectedKeyOnThisPath && this.unlockedPath === path
  }

  /**
   * Checks if WebHID transport is supported by the browser.
   * Note: This API is not available in service workers in manifest v3.
   */
  static isSupported = () =>
    typeof navigator !== 'undefined' && navigator !== null && 'hid' in navigator

  /**
   * Checks if at least one Ledger device is connected.
   */
  static isConnected = async () => {
    if (!('hid' in navigator)) return false

    const devices = await navigator.hid.getDevices()
    const hasFoundLedgerDevice =
      devices.filter((device) => device.vendorId === LedgerController.vendorId).length > 0
    if (hasFoundLedgerDevice) return true

    // TODO: Temporarily bypass the device detection for Vivaldi, because although
    // the device is connected, the getDevices() method returns an empty array.
    // Workarounds I tried - find the device via DMK didn't click. So to be able
    // to use Ledger device with Vivaldi, we just assume it's connected.
    // IMPORTANT: This is fixed in https://vivaldi.com/blog/desktop/customizable-tab-bar-vivaldi-browser-snapshot-3704-3/
    // so once this version is released, we should remove this check.
    return isVivaldi()
  }

  /**
   * Grant permission to the extension service worker to access an HID device.
   * Should be called only from the foreground and it requires a user gesture
   * to open the device selection prompt (click on a button, etc.).
   */
  static grantDevicePermissionIfNeeded = async () => {
    const dmk = new DeviceManagementKitBuilder()
      // .addLogger(new ConsoleLogger()) // for debugging only
      .addTransport(webHidTransportFactory)
      .build()

    return new Promise((resolve, reject) => {
      // Start discovering - this will scan for any connected devices
      let subscription: Subscription // so it is always defined inside the subscribe callback
      // eslint-disable-next-line prefer-const
      subscription = dmk.startDiscovering({}).subscribe({
        next: async (device) => {
          subscription.unsubscribe()
          dmk.close()
          resolve(device)
        },
        error: (error) => {
          subscription.unsubscribe()
          reject(new ExternalSignerError(normalizeLedgerMessage(error?.message)))
        }
      })
    })
  }

  /**
   * This function is designed to handle the scenario where Ledger device loses
   * connectivity during an operation. Without this method, if the Ledger device
   * disconnects, the observable returned by the Ledger SDK throws an error,
   * but with an awkward delay.
   *
   * Was removed in v5.24.0, because after an update of the Ledger DMK - the
   * observable returned by the Ledger SDK started to throw WITHOUT an awkward
   * delay there was. But then brought back in v5.33.5, because after another
   * update of the Ledger DMK - the DMK behavior turned out to be inconsistent
   * (not working while in the middle of signing a raw transaction).
   */
  withDisconnectProtection = async <T>(operation: () => Promise<T>): Promise<T> => {
    let listenerCbRef: (...args: Array<any>) => any = () => {}
    const disconnectHandler =
      (reject: (reason?: any) => void) =>
      async ({ device }: { device: HIDDevice }) => {
        if (LedgerController.vendorId === device.vendorId) {
          await this.cleanUp() // the communication with the initiated walletSDK drops
          reject(new ExternalSignerError('Ledger device got disconnected.'))
        }
      }

    try {
      // Race the operation against a new Promise that rejects if a 'disconnect'
      // event is emitted from the Ledger device. If the device disconnects
      // before the operation completes, the new Promise rejects and the method
      // returns, preventing the SDK from hanging. If the operation completes
      // before the device disconnects, the result of the operation is returned.
      const result = await Promise.race<T>([
        operation(),
        new Promise((_, reject) => {
          listenerCbRef = disconnectHandler(reject)
          navigator.hid.addEventListener('disconnect', listenerCbRef)
        })
      ])

      return result
    } finally {
      // In either case, the 'disconnect' event listener should be removed
      // after the operation to clean up resources.
      if (listenerCbRef) navigator.hid.removeEventListener('disconnect', listenerCbRef)
    }
  }

  /**
   * Handles the scenario where Ledger device hangs indefinitely during an
   * operation. Happens in some cases when the Ledger device gets connected to
   * Ambire, then another app is accessing the Ledger device and then user comes
   * back to Ambire (without unplug-plugging the Ledger).
   */
  static withTimeoutProtection = async <T>(operation: () => Promise<T>): Promise<T> => {
    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => {
        const message = translation.t(
          'Could not connect to your Ledger device for an extended period. Please close any other apps that may be accessing your Ledger device (including wallet apps on your computer and web apps). Ensure your Ledger is responsive. Unplug and plug it again.'
        )
        reject(new ExternalSignerError(message))
      }, TIMEOUT_FOR_RETRIEVING_FROM_LEDGER)
    })

    return Promise.race([operation(), timeout])
  }

  /**
   * The Ledger device requires a new SDK instance (session) every time the
   * device is connected (after being disconnected). This method checks if there
   * is an existing SDK instance and creates a new one if needed.
   */
  async #initSDKSessionIfNeeded() {
    const isConnected = await LedgerController.isConnected()
    if (!isConnected)
      throw new ExternalSignerError(
        translation.t("Ledger is not connected. Please make sure it's plugged in.")
      )

    if (this.walletSDK) return

    try {
      this.walletSDK = new DeviceManagementKitBuilder()
        // .addLogger(new ConsoleLogger()) // for debugging only
        .addTransport(webHidTransportFactory)
        .build()

      const device = await this.#findDevice()
      if (!device) throw new Error('No Ledger device detected.')
      if (!this.walletSDK) throw new Error('Connection to Ledger device lost.')

      // Get device information
      const sessionId = await this.walletSDK.connect({ device })
      this.#sessionId = sessionId
      const connectedDevice = this.walletSDK.getConnectedDevice({ sessionId })
      this.deviceModel = connectedDevice.modelId
      this.deviceId = connectedDevice.id

      // Create the signer using the dynamically imported constructor
      const contextModule = new ContextModuleBuilder({ originToken: 'ambire' }).build()
      this.signerEth = new SignerEthBuilder({ dmk: this.walletSDK, sessionId })
        .withContextModule(contextModule)
        .build()

      // To clean up no matter if the device is in the middle of an operation or not
      navigator.hid.addEventListener('disconnect', this.cleanUpListener)
    } catch (e: any) {
      throw new ExternalSignerError(normalizeLedgerMessage(e?.message))
    }
  }

  #findDevice = () =>
    new Promise<DiscoveredDevice>((resolve, reject) => {
      let subscription: Subscription // so it is always defined inside the subscribe callback
      let isCancelled = false
      // eslint-disable-next-line prefer-const
      subscription = this.walletSDK!.listenToAvailableDevices({}).subscribe({
        next: (devices) => {
          if (isCancelled) return
          if (devices && devices.length) {
            subscription.unsubscribe()
            // TODO: Multiple devices found?
            resolve(devices[0])
          }
        },
        error: (error) => {
          if (isCancelled) return
          subscription.unsubscribe()
          reject(new Error(error?.message))
        }
      })

      this.#rejectSigningSubscription = () => {
        isCancelled = true
        subscription.unsubscribe()
        reject(new ExternalSignerError('Operation cancelled by user'))
      }
    })

  /**
   * Helper method to handle common Ledger observable subscription patterns
   */
  #handleLedgerSubscription<T>(
    observable: Observable<any>,
    options: {
      onCompleted: (output: any) => T
      errorMessage: string
      isSign?: boolean
      /**
       * Called on every intermediate step reported by the Ledger device action.
       * Errors emitted by the device action do not carry the step they occurred
       * on, so callers that need it must track the last reported one.
       */
      onStep?: (step: string) => void
    }
  ): Promise<T> {
    const { onCompleted, errorMessage, isSign, onStep } = options

    const subscriptionPromise = new Promise<T>((resolve, reject) => {
      let subscription: Subscription // so it is always defined inside the subscribe callback
      let isCancelled = false
      // Tracked for diagnostics even when the caller passes no `onStep`: the
      // step an error happened on is the single most useful fact about a failed
      // Ledger signature, and the device action does not carry it on the error.
      let lastReportedStep: string | null = null

      // eslint-disable-next-line prefer-const
      subscription = observable.subscribe({
        next: (response: any) => {
          if (isCancelled) return
          if (response.status === 'pending' && response.intermediateValue?.step) {
            lastReportedStep = response.intermediateValue.step
            if (onStep) onStep(response.intermediateValue.step)
          }
          // TODO: If we communicate this to the user in the UI better, we can
          // wait for the user to do all required interactions instead of rejecting.
          const missingRequiredUserInteraction =
            response.status === 'pending' &&
            [UserInteractionRequired.UnlockDevice, UserInteractionRequired.ConfirmOpenApp].includes(
              response.intermediateValue.requiredUserInteraction
            )

          if (missingRequiredUserInteraction) {
            subscription.unsubscribe()
            reject(
              new ExternalSignerError(
                normalizeLedgerMessage(response.intermediateValue.requiredUserInteraction)
              )
            )
          }

          if (response.status === 'error') {
            subscription.unsubscribe()
            // @ts-ignore Ledger types not being resolved correctly in the SDK
            const deviceMessage =
              response.error?.message || response.error?._tag || 'no response from device'
            // @ts-ignore Ledger types not being resolved correctly in the SDK
            const deviceErrorCode = response.error?.errorCode
            let message = `<${deviceMessage}>`
            message = deviceErrorCode ? `${message}, error code: <${deviceErrorCode}>` : message
            return reject(
              LedgerController.#withDeviceDiagnostics(
                new ExternalSignerError(normalizeLedgerMessage(message)),
                { deviceErrorCode, deviceMessage, step: lastReportedStep }
              )
            )
          }

          if (response.status !== 'completed') return

          subscription.unsubscribe()
          if (response?.output) {
            resolve(onCompleted(response.output))
          } else {
            reject(new ExternalSignerError(normalizeLedgerMessage(errorMessage)))
          }
        },
        error: (error: any) => {
          subscription.unsubscribe()
          reject(new ExternalSignerError(normalizeLedgerMessage(error?.message)))
        }
      })

      if (isSign) {
        this.#rejectSigningSubscription = () => {
          isCancelled = true
          subscription.unsubscribe()
          reject(new ExternalSignerError('Operation cancelled by user'))
        }
      }
    })

    return this.withDisconnectProtection(() => subscriptionPromise)
  }

  async unlock(
    path: ReturnType<typeof getHdPathFromTemplate>,
    expectedKeyOnThisPath?: string
  ): Promise<'ALREADY_UNLOCKED' | 'JUST_UNLOCKED'> {
    await this.#initSDKSessionIfNeeded()

    if (this.isUnlocked(path, expectedKeyOnThisPath)) return 'ALREADY_UNLOCKED'

    if (!this.isWebHID)
      throw new ExternalSignerError(
        translation.t(
          'Ledger only supports USB connection between ZKX and your device. Please connect your device via USB.'
        )
      )

    if (!this.walletSDK || !this.signerEth) throw new ExternalSignerError(normalizeLedgerMessage()) // no message, indicating no connection

    const address = await this.#handleLedgerSubscription(
      this.signerEth!.getAddress(getHdPathWithoutRoot(path), {
        checkOnDevice: false,
        returnChainCode: false
      }).observable,
      {
        onCompleted: (output) => output.address,
        errorMessage: 'Failed to get address from Ledger device'
      }
    )

    // Save unlocked state
    this.unlockedPath = path
    this.unlockedPathKeyAddr = address

    return 'JUST_UNLOCKED' as const
  }

  retrieveAddresses = async (paths: string[]) => {
    await this.#initSDKSessionIfNeeded() // could happen if user retries

    if (!this.walletSDK || !this.signerEth) {
      throw new ExternalSignerError(normalizeLedgerMessage())
    }

    const keys: string[] = []
    let latestGetAddressError: ExternalSignerError | undefined

    for (let i = 0; i < paths.length; i++) {
      try {
        // Purposely await in loop to avoid sending multiple requests at once.
        // Send them 1 by 1, the Ledger device can't handle them in parallel,
        // it throws a "device busy" error.
        // eslint-disable-next-line no-await-in-loop
        const key = await this.#handleLedgerSubscription(
          this.signerEth!.getAddress(getHdPathWithoutRoot(paths[i]), {
            checkOnDevice: false,
            returnChainCode: false
          }).observable,
          {
            onCompleted: (output) => output.address,
            errorMessage: 'Failed to get address from Ledger device'
          }
        )

        keys.push(key)
      } catch (e: any) {
        latestGetAddressError = e
        break
      }
    }

    // Corner-case: if user interacts with Ambire, then interacts with another
    // wallet app (installed on the computer or a web app) and then comes back
    // to Ambire, the Ledger device might not respond to all requests. Therefore
    // we might receive only some of the keys, but not all of them.
    // To reproduce: 1. Plug in and unlock Ledger, open Ambire, import accounts;
    // 2. Now switch to Ledger Live and connect (My Ledger); 3. Switch back to
    // Ambire and try importing accounts again.
    const notAllKeysGotRetrieved = keys.length !== paths.length
    if (notAllKeysGotRetrieved) {
      throw (
        latestGetAddressError ||
        new ExternalSignerError(translation.t('Failed to get all addresses from Ledger device.'))
      )
    }

    return keys
  }

  async signPersonalMessage(derivationPath: string, messageHex: string) {
    if (!this.signerEth) throw new ExternalSignerError(normalizeLedgerMessage())

    const messageBytes = hexStringToUint8Array(messageHex)

    return this.#handleLedgerSubscription<LedgerSignature>(
      this.signerEth.signMessage(getHdPathWithoutRoot(derivationPath), messageBytes).observable,
      {
        onCompleted: (output) => output,
        errorMessage: 'Failed to sign message with Ledger device',
        isSign: true
      }
    )
  }

  /**
   * Keep the RAW device failure on the thrown error, and log it.
   *
   * `normalizeLedgerMessage` turns a status word into a sentence for the user,
   * which is all the UI needs but destroys the only evidence a bug report can
   * be diagnosed from: the same "Blind Signing is disabled" sentence is emitted
   * for 0x6985 at SIGN_TRANSACTION (blind signing genuinely off on the device)
   * and for 0x6a80 while providing clear-signing contexts (a metadata problem
   * the blind-sign fallback below is supposed to absorb). Without the code and
   * the step, those two are indistinguishable from a screenshot - which is
   * exactly how this got misdiagnosed once already.
   */
  static #withDeviceDiagnostics(
    error: ExternalSignerError,
    diagnostics: { deviceErrorCode?: string; deviceMessage?: string; step: string | null }
  ): ExternalSignerError {
    Object.assign(error, {
      deviceErrorCode: diagnostics.deviceErrorCode,
      deviceRawMessage: diagnostics.deviceMessage,
      deviceStep: diagnostics.step
    })
    console.error(
      `[ledger] device action failed - step: <${diagnostics.step ?? 'none reported'}>, error code: <${
        diagnostics.deviceErrorCode ?? 'none'
      }>, raw message: <${diagnostics.deviceMessage ?? 'none'}>`
    )

    return error
  }

  /**
   * A context module that never resolves any clear-signing context, so that the
   * Ledger device action skips the whole "provide contexts" phase altogether
   * and goes straight to blind signing.
   */
  static #buildNoClearSigningContextModule = (): ContextModule =>
    ({
      getContexts: async () => [],
      getFieldContext: async () => ({
        type: ClearSignContextType.ERROR,
        error: new Error('Clear signing intentionally skipped')
      }),
      getTypedDataFilters: async () => ({ messageInfo: undefined, filters: [] }),
      getSolanaContext: async () => ({})
    }) as unknown as ContextModule

  #getBlindSignerEth() {
    if (this.#blindSignerEth) return this.#blindSignerEth
    if (!this.walletSDK || !this.#sessionId) return null

    this.#blindSignerEth = new SignerEthBuilder({
      dmk: this.walletSDK,
      sessionId: this.#sessionId
    })
      .withContextModule(LedgerController.#buildNoClearSigningContextModule())
      .build()

    return this.#blindSignerEth
  }

  async signTransaction(derivationPath: string, transaction: Uint8Array) {
    if (!this.signerEth) throw new ExternalSignerError(normalizeLedgerMessage())

    // The Ledger device action fails hard - with no built-in blind signing
    // fallback - when any of the clear-signing context APDUs gets rejected by
    // the Ethereum app. That happens on networks/contracts Ledger has partial
    // or broken clear-signing metadata for (e.g. an ERC-20 approval on
    // PulseChain). The device rejects before the user gets to confirm anything
    // and the status word it returns (0x6985/0x6a80) is indistinguishable from
    // "blind signing is disabled" - so the user sees that (wrong) message even
    // with blind signing already enabled. Therefore, if the flow never reached
    // the actual signing step, retry the very same transaction once with clear
    // signing turned off, which is exactly what a blind signature needs.
    let lastStep: string | null = null

    try {
      return await this.#handleLedgerSubscription<LedgerSignature>(
        this.signerEth.signTransaction(getHdPathWithoutRoot(derivationPath), transaction)
          .observable,
        {
          onCompleted: (output) => output,
          errorMessage: 'Failed to sign transaction with Ledger device',
          isSign: true,
          onStep: (step) => {
            lastStep = step
          }
        }
      )
    } catch (error: any) {
      // The helper records the step on the error too, which is authoritative -
      // `lastStep` only sees steps this closure was still subscribed for.
      const failedOnStep: string | null = error?.deviceStep ?? lastStep
      const blindSignerEth = this.#getBlindSignerEth()

      // Rejections (or timeouts) that happened on the device itself, as well as
      // connectivity errors, must be surfaced as-is - retrying would only
      // re-prompt the user for something they already declined.
      if (!shouldRetryWithBlindSigning({ failedOnStep, errorMessage: error?.message }))
        throw error
      if (!blindSignerEth) throw error

      return await this.#handleLedgerSubscription<LedgerSignature>(
        blindSignerEth.signTransaction(getHdPathWithoutRoot(derivationPath), transaction)
          .observable,
        {
          onCompleted: (output) => output,
          errorMessage: 'Failed to sign transaction with Ledger device',
          isSign: true
        }
      )
    }
  }

  /**
   * Attempts to sign an EIP-712 message using the Ledger device. With Ledger DSK lib,
   * if the device does not support direct (clear) EIP-712 signing, it should internally
   * fallback to signing hashes of the message, no additional steps needed.
   */
  signTypedData = async ({
    path,
    signTypedData: { domain, types, message, primaryType }
  }: {
    path: string
    signTypedData: TypedMessageUserRequest['meta']['params']
  }) => {
    if (!this.signerEth) throw new ExternalSignerError(normalizeLedgerMessage())
    // TODO: Slight mismatch between TypedMessage type and Ledger's TypedDataDomain
    // for the empty values (string | null | undefined vs string | undefined)
    const ledgerDomain = { ...domain } as TypedDataDomain

    return this.#handleLedgerSubscription<LedgerSignature>(
      this.signerEth.signTypedData(getHdPathWithoutRoot(path), {
        domain: ledgerDomain,
        types,
        message,
        primaryType
      }).observable,
      {
        onCompleted: (output) => output,
        errorMessage: 'Failed to sign typed data with Ledger device',
        isSign: true
      }
    )
  }

  async signingCleanup() {
    if (!this.#rejectSigningSubscription) return

    this.#rejectSigningSubscription()
    this.#rejectSigningSubscription = null
  }

  cleanUp = async () => {
    if (this.walletSDK) this.walletSDK.close()

    this.walletSDK = null
    this.signerEth = null
    this.#blindSignerEth = null
    this.#sessionId = null
    this.unlockedPath = ''
    this.unlockedPathKeyAddr = ''

    if ('hid' in navigator) navigator.hid.removeEventListener('disconnect', this.cleanUpListener)
  }

  async cleanUpListener({ device }: { device: HIDDevice }) {
    if (device.vendorId === LedgerController.vendorId) await this.cleanUp()
  }
}

export default LedgerController
